// src/components/inventory/InventoryCreateModal.js
// DROP-IN REPLACEMENT - FIXED FORM SUBMISSION
//
// Changes applied:
// ✅ Fixed form submission - removed onClick from footer button
// ✅ Fixed input focus issue (form was re-submitting on every keystroke)
// ✅ Visibility is groupIds-only.
// ✅ Added "Clear Groups" button (clearing => admin/owner only by rules).
// ✅ Default on create: grp_all_staff selected when available.

import React, { useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { X, PlusCircle, Shield, Eye, Package, Users } from "lucide-react";

const uniqStrings = (arr) =>
  Array.from(new Set((Array.isArray(arr) ? arr : []).filter(Boolean).map(String)));
const asBool = (v) => v === true || v === "true" || v === 1 || v === "1";

// Moved Row component outside to prevent re-renders
const Row = ({ label, required, children }) => (
  <div className="min-w-0">
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-2">
      <label className="text-[11px] font-medium text-slate-700">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>
      <div className="min-w-0">{children}</div>
    </div>
  </div>
);

const InventoryCreateModal = ({ isOpen, onClose, companyId, onCreated }) => {
  const formId = "inventory-create-form";
  const db = getFirestore();
  const auth = getAuth();
  const storage = getStorage();

  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState("");
  const [resolvedCompanyId, setResolvedCompanyId] = useState(companyId || "");
  const [myRole, setMyRole] = useState("");
  const [companySettings, setCompanySettings] = useState({});

  const [groups, setGroups] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");

  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageError, setImageError] = useState("");

  const [form, setForm] = useState({
    sku: "",
    name: "",
    category: "",
    subCategory: "",
    brand: "",
    packSize: "",
    price: "",
    stock: "",
    lowStockThreshold: "10",
    sampleable: true, // single checkbox
    featured: false,
    seasonal: false,
    contractItem: false,
    customerVisible: true,
    description: "",
    customerFacingDescription: "",
    internalNotes: "",
    countryOfOrigin: "",
    rebateDetails: "",
    volumeDiscountDetails: "",
    contractDetails: "",
    groupIds: [],
  });

  const canShow = !!isOpen;
  const effectiveCompanyId = resolvedCompanyId || companyId || "";
  const showPricingFlag = useMemo(() => {
    const cs = companySettings || {};
    const flags = cs.featureFlags || {};
    return asBool(flags.showInventoryPricingToEndUsers ?? cs.showInventoryPricingToEndUsers);
  }, [companySettings]);
  const onlyOwnerCanSeePricing = useMemo(() => {
    const cs = companySettings || {};
    const flags = cs.featureFlags || {};
    return asBool(flags.onlyOwnerCanSeeInventoryPricing);
  }, [companySettings]);
  const canSeePricing = useMemo(() => {
    if (onlyOwnerCanSeePricing) return myRole === "owner";
    return myRole === "owner" || myRole === "admin" || myRole === "super_admin" || showPricingFlag;
  }, [myRole, onlyOwnerCanSeePricing, showPricingFlag]);
  const commercialTermsVisibility = useMemo(() => {
    const cs = companySettings || {};
    const flags = cs.featureFlags || {};
    const value = String(flags.commercialTermsVisibility || "none").trim().toLowerCase();
    return ["none", "admin", "all"].includes(value) ? value : "none";
  }, [companySettings]);
  const canSeeCommercialTerms = useMemo(() => {
    if (myRole === "owner") return true;
    if (commercialTermsVisibility === "all") return true;
    if (commercialTermsVisibility === "admin") {
      return myRole === "admin" || myRole === "super_admin";
    }
    return false;
  }, [commercialTermsVisibility, myRole]);

  useEffect(() => {
    setResolvedCompanyId(companyId || "");
  }, [companyId]);

  useEffect(() => {
    const resolveCompanyId = async () => {
      if (!isOpen) return;

      const uid = auth.currentUser?.uid || null;
      if (!uid) return;

      try {
        const userSnap = await getDoc(doc(db, "users", uid));
        if (!userSnap.exists()) return;
        const userData = userSnap.data() || {};
        setMyRole(String(userData.role || "").trim().toLowerCase());
        if (!companyId) {
          const nextCompanyId = String(userData?.companyId || "").trim();
          if (nextCompanyId) setResolvedCompanyId(nextCompanyId);
        }
      } catch (err) {
        console.error("[InventoryCreateModal] resolve companyId failed:", err);
      }
    };

    resolveCompanyId();
  }, [auth, companyId, db, isOpen]);

  useEffect(() => {
    const loadCompanySettings = async () => {
      if (!isOpen || !effectiveCompanyId) return;
      try {
        const companySnap = await getDoc(doc(db, "companies", effectiveCompanyId));
        setCompanySettings(companySnap.exists() ? companySnap.data() || {} : {});
      } catch (err) {
        console.warn("[InventoryCreateModal] Could not load company settings:", err);
        setCompanySettings({});
      }
    };

    loadCompanySettings();
  }, [db, effectiveCompanyId, isOpen]);

  const allStaffGroupId = useMemo(() => {
    const g = groups.find(
      (x) =>
        x.id === "grp_all_staff" ||
        x.key === "grp_all_staff" ||
        x.slug === "grp_all_staff" ||
        (x.name || "").toLowerCase() === "all staff"
    );
    return g?.id ? String(g.id) : "";
  }, [groups]);

  const selectedGroupIds = useMemo(() => uniqStrings(form.groupIds), [form.groupIds]);

  const filteredGroups = useMemo(() => {
    const q = (groupSearch || "").toLowerCase().trim();
    if (!q) return groups;
    return groups.filter((g) => (g?.name || "").toLowerCase().includes(q));
  }, [groups, groupSearch]);

  const toggleGroup = (gid) => {
    const id = String(gid);
    setForm((prev) => {
      const set = new Set(uniqStrings(prev.groupIds));
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, groupIds: Array.from(set) };
    });
  };

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const clearGroups = () => {
    setForm((prev) => ({ ...prev, groupIds: [] }));
    setGroupSearch("");
  };

  const setAllStaff = () => {
    if (!allStaffGroupId) return;
    setForm((prev) => ({ ...prev, groupIds: [allStaffGroupId] }));
    setGroupSearch("");
  };

  const handleImageSelect = (file) => {
    setImageError("");
    if (!file) {
      setImageFile(null);
      setImagePreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
      return;
    }

    if (!file.type?.startsWith("image/")) {
      setImageFile(null);
      setImagePreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
      setImageError("Please choose an image file.");
      return;
    }

    setImageFile(file);
    const url = URL.createObjectURL(file);
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
  };

  // Load groups when modal opens and auto-select grp_all_staff
  useEffect(() => {
    const loadGroups = async () => {
      if (!isOpen || !effectiveCompanyId) return;

      try {
        const snap = await getDocs(collection(db, "companies", effectiveCompanyId, "groups"));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const sysA = a.isSystem ? 0 : 1;
            const sysB = b.isSystem ? 0 : 1;
            if (sysA !== sysB) return sysA - sysB;
            return (a.name || "")
              .toLowerCase()
              .localeCompare((b.name || "").toLowerCase());
          });

        setGroups(list);

        const g = list.find(
          (x) =>
            x.id === "grp_all_staff" ||
            x.key === "grp_all_staff" ||
            x.slug === "grp_all_staff" ||
            (x.name || "").toLowerCase() === "all staff"
        );

        setForm((prev) => {
          if (uniqStrings(prev.groupIds).length > 0) return prev;
          if (!g?.id) return prev;
          return { ...prev, groupIds: [String(g.id)] };
        });
      } catch (e) {
        console.error("[InventoryCreateModal] load groups failed:", e);
        setGroups([]);
      }
    };

    loadGroups();
  }, [db, effectiveCompanyId, isOpen]);

  // Reset when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setError("");
    setSaving(false);
    setUploadingImage(false);
    setGroupSearch("");

    setImageError("");
    setImageFile(null);
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });

    setForm({
      sku: "",
      name: "",
      category: "",
      subCategory: "",
      brand: "",
      packSize: "",
      price: "",
      stock: "",
      lowStockThreshold: "10",
      sampleable: true,
      featured: false,
      seasonal: false,
      contractItem: false,
      customerVisible: true,
      description: "",
      customerFacingDescription: "",
      internalNotes: "",
      countryOfOrigin: "",
      rebateDetails: "",
      volumeDiscountDetails: "",
      contractDetails: "",
      groupIds: allStaffGroupId ? [allStaffGroupId] : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setImageError("");

    if (!effectiveCompanyId) return setError("Missing companyId.");
    if (!form.name.trim()) return setError("Product name is required.");
    if (!form.sku.trim()) return setError("SKU is required.");
    if (uploadingImage) return;

    setSaving(true);

    try {
      const uid = auth.currentUser?.uid || null;

      const finalGroups = uniqStrings(selectedGroupIds);
      const sampleable = !!form.sampleable;

      const payload = {
        sku: form.sku.trim() || null,
        name: form.name.trim(),
        category: form.category.trim() || null,
        previousSkus: [],
        subCategory: form.subCategory.trim() || null,
        brand: form.brand.trim() || null,
        packSize: form.packSize.trim() || null,
        price: form.price !== "" ? Number(form.price) : null,
        stock: form.stock !== "" ? Number(form.stock) : 0,
        lowStockThreshold: form.lowStockThreshold !== "" ? Number(form.lowStockThreshold) : 10,

        // sampling (single checkbox)
        sampleable,
        // keep legacy field synced (no UI)
        unsampleable: !sampleable,

        featured: !!form.featured,
        seasonal: !!form.seasonal,
        contractItem: !!form.contractItem,

        description: form.description.trim() || null,
        customerFacingDescription: form.customerFacingDescription.trim() || null,
        internalNotes: form.internalNotes.trim() || null,
        countryOfOrigin: form.countryOfOrigin.trim() || null,
        rebateDetails: form.rebateDetails.trim() || null,
        volumeDiscountDetails: form.volumeDiscountDetails.trim() || null,
        contractDetails: form.contractDetails.trim() || null,

        imageUrl: null,

        customerVisible: !!form.customerVisible,
        hiddenFromCustomer: !form.customerVisible,

        // ✅ group-only visibility
        groupIds: finalGroups,

        companyId: effectiveCompanyId,
        createdAt: serverTimestamp(),
        createdBy: uid,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      };

      const colRef = collection(db, "companies", effectiveCompanyId, "inventory");
      const created = await addDoc(colRef, payload);

      // Upload image if provided (after create so we have id)
      if (imageFile) {
        setUploadingImage(true);
        try {
          const imageRef = ref(
            storage,
            `companies/${effectiveCompanyId}/inventory/${created.id}/main_${Date.now()}_${imageFile.name}`
          );
          await uploadBytes(imageRef, imageFile);
          const downloadURL = await getDownloadURL(imageRef);

          await updateDoc(doc(db, "companies", effectiveCompanyId, "inventory", created.id), {
            imageUrl: downloadURL,
            updatedAt: serverTimestamp(),
            updatedBy: uid,
          });
        } catch (imgErr) {
          console.error("[InventoryCreateModal] image upload failed:", imgErr);
          setImageError("Product created, but image upload failed.");
        } finally {
          setUploadingImage(false);
        }
      }

      onCreated?.(created.id);
      onClose?.();
    } catch (err) {
      console.error("[InventoryCreateModal] create failed:", err);
      setError(err?.message || "Failed to create product.");
    } finally {
      setSaving(false);
    }
  };

  if (!canShow) return null;

  const selectedCount = selectedGroupIds.length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-950/60" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-600/10 flex items-center justify-center">
              <PlusCircle className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <div className="text-base font-semibold text-slate-900">Add Product</div>
              <div className="text-xs text-slate-500">
                Create a new inventory item with group visibility controls
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Body */}
        <form id={formId} onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* LEFT */}
            <div className="lg:col-span-2 space-y-3">
              {/* Product Info */}
              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="text-sm font-semibold text-slate-900 mb-2">Product Information</div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="md:col-span-2">
                    <Row label="Name" required>
                      <input
                        type="text"
                        className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        placeholder="e.g. Smoked Salmon"
                        required
                      />
                    </Row>
                  </div>

                  <Row label="SKU" required>
                    <input
                      type="text"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.sku}
                      onChange={(e) => setField("sku", e.target.value)}
                      placeholder="Required"
                      required
                    />
                  </Row>

                  <Row label="Category">
                    <input
                      type="text"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.category}
                      onChange={(e) => setField("category", e.target.value)}
                      placeholder="Optional"
                    />
                  </Row>

                  <Row label="Subcategory">
                    <input
                      type="text"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.subCategory}
                      onChange={(e) => setField("subCategory", e.target.value)}
                      placeholder="e.g. Shellfish"
                    />
                  </Row>

                  <Row label="Brand">
                    <input
                      type="text"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.brand}
                      onChange={(e) => setField("brand", e.target.value)}
                      placeholder="Optional brand"
                    />
                  </Row>

                  <Row label="Pack Size">
                    <input
                      type="text"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.packSize}
                      onChange={(e) => setField("packSize", e.target.value)}
                      placeholder="e.g. 12/16oz"
                    />
                  </Row>

                  <Row label="Origin">
                    <input
                      type="text"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.countryOfOrigin}
                      onChange={(e) => setField("countryOfOrigin", e.target.value)}
                      placeholder="e.g. USA"
                    />
                  </Row>

                  {canSeePricing ? (
                  <Row label="Price">
                    <input
                      type="number"
                      step="0.01"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.price}
                      onChange={(e) => setField("price", e.target.value)}
                      placeholder="0.00"
                    />
                  </Row>
                  ) : null}

                  <Row label="Stock">
                    <input
                      type="number"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.stock}
                      onChange={(e) => setField("stock", e.target.value)}
                      placeholder="0"
                    />
                  </Row>

                  <Row label="Low Stock">
                    <input
                      type="number"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.lowStockThreshold}
                      onChange={(e) => setField("lowStockThreshold", e.target.value)}
                      placeholder="10"
                    />
                  </Row>

                  <div className="md:col-span-2">
                    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 items-start">
                      <label className="text-[11px] font-medium text-slate-700 pt-2">
                        Internal Description
                      </label>
                      <textarea
                        className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        rows={3}
                        value={form.description}
                        onChange={(e) => setField("description", e.target.value)}
                        placeholder="Internal product notes or summary"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 items-start">
                      <label className="text-[11px] font-medium text-slate-700 pt-2">
                        Customer Description
                      </label>
                      <textarea
                        className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        rows={2}
                        value={form.customerFacingDescription}
                        onChange={(e) => setField("customerFacingDescription", e.target.value)}
                        placeholder="What customers should see in the catalog"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 items-start">
                      <label className="text-[11px] font-medium text-slate-700 pt-2">
                        Internal Notes
                      </label>
                      <textarea
                        className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        rows={2}
                        value={form.internalNotes}
                        onChange={(e) => setField("internalNotes", e.target.value)}
                        placeholder="Private notes for your team"
                      />
                    </div>
                  </div>

                  {canSeeCommercialTerms ? (
                    <>
                      <div className="md:col-span-2">
                        <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 items-start">
                          <label className="text-[11px] font-medium text-slate-700 pt-2">
                            Rebates
                          </label>
                          <textarea
                            className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            rows={2}
                            value={form.rebateDetails}
                            onChange={(e) => setField("rebateDetails", e.target.value)}
                            placeholder="Optional rebate details"
                          />
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 items-start">
                          <label className="text-[11px] font-medium text-slate-700 pt-2">
                            Volume Discounts
                          </label>
                          <textarea
                            className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            rows={2}
                            value={form.volumeDiscountDetails}
                            onChange={(e) => setField("volumeDiscountDetails", e.target.value)}
                            placeholder="Optional volume discount terms"
                          />
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 items-start">
                          <label className="text-[11px] font-medium text-slate-700 pt-2">
                            Contracts
                          </label>
                          <textarea
                            className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            rows={2}
                            value={form.contractDetails}
                            onChange={(e) => setField("contractDetails", e.target.value)}
                            placeholder="Optional contract notes"
                          />
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Toggles */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
                <label className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-slate-200 bg-white cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-blue-700" />
                    <div className="leading-tight">
                      <div className="text-sm font-medium text-slate-900">Sampleable</div>
                      <div className="text-xs text-slate-500">Can be requested</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!form.sampleable}
                    onChange={(e) => setField("sampleable", e.target.checked)}
                  />
                </label>

                <label className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-slate-200 bg-white cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-700" />
                    <div className="leading-tight">
                      <div className="text-sm font-medium text-slate-900">Featured</div>
                      <div className="text-xs text-slate-500">Highlight in catalog</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!form.featured}
                    onChange={(e) => setField("featured", e.target.checked)}
                  />
                </label>

                <label className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-slate-200 bg-white cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-emerald-700" />
                    <div className="leading-tight">
                      <div className="text-sm font-medium text-slate-900">Customer Visible</div>
                      <div className="text-xs text-slate-500">Customers can see it</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!form.customerVisible}
                    onChange={(e) => setField("customerVisible", e.target.checked)}
                  />
                </label>

                <label className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-slate-200 bg-white cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-sky-700" />
                    <div className="leading-tight">
                      <div className="text-sm font-medium text-slate-900">Seasonal</div>
                      <div className="text-xs text-slate-500">Mark as seasonal</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!form.seasonal}
                    onChange={(e) => setField("seasonal", e.target.checked)}
                  />
                </label>

                <label className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-slate-200 bg-white cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-violet-700" />
                    <div className="leading-tight">
                      <div className="text-sm font-medium text-slate-900">Contract Item</div>
                      <div className="text-xs text-slate-500">Contract or committed buy</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!form.contractItem}
                    onChange={(e) => setField("contractItem", e.target.checked)}
                  />
                </label>
              </div>
            </div>

            {/* RIGHT */}
            <div className="space-y-3">
              {/* Image */}
              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-900">Product Image</div>
                  <div className="text-xs text-slate-500">Optional</div>
                </div>

                {imagePreviewUrl ? (
                  <div className="mb-2 border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
                    <img src={imagePreviewUrl} alt="Preview" className="w-full h-44 object-cover" />
                  </div>
                ) : (
                  <div className="mb-2 text-xs text-slate-500">No image selected</div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  className="block w-full text-xs text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  onChange={(e) => handleImageSelect(e.target.files && e.target.files[0])}
                />

                {imageError ? <div className="mt-2 text-xs text-amber-700">{imageError}</div> : null}
              </div>

              {/* Group Visibility */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-700" />
                    <div className="text-sm font-semibold text-slate-900">Group Visibility</div>
                  </div>

                  <div className="flex items-center gap-2">
                    {allStaffGroupId ? (
                      <button
                        type="button"
                        onClick={setAllStaff}
                        className="text-[11px] px-2 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                        title="Set visibility to All Staff"
                      >
                        All Staff
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={clearGroups}
                      className="text-[11px] px-2 py-1 rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                      title="Clear all groups (Admin/Owner only)"
                    >
                      Clear Groups
                    </button>
                  </div>
                </div>

                <div className="text-[11px] text-slate-600 mb-2">
                  End Users can only see items if their <strong>groupIds</strong> intersect this list.
                </div>

                <div className="text-xs text-slate-600 mb-2">
                  Selected: <span className="font-semibold text-blue-600">{selectedCount}</span>
                </div>

                {groups.length > 0 && (
                  <input
                    type="text"
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    placeholder="Search groups..."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}

                {groups.length === 0 ? (
                  <div className="text-xs text-slate-500 text-center py-4">No groups found yet.</div>
                ) : filteredGroups.length === 0 ? (
                  <div className="text-xs text-slate-500 text-center py-4">No groups match your search.</div>
                ) : (
                  <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-xl bg-white">
                    <ul className="divide-y divide-slate-100">
                      {filteredGroups.map((g) => {
                        const checked = selectedGroupIds.includes(String(g.id));
                        const isAllStaff =
                          g.id === "grp_all_staff" ||
                          g.key === "grp_all_staff" ||
                          (g.name || "").toLowerCase() === "all staff";

                        return (
                          <li key={g.id} className="px-3 py-2 hover:bg-slate-50">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                className="w-4 h-4"
                                checked={checked}
                                onChange={() => toggleGroup(g.id)}
                              />
                              <span className="flex-1">
                                {g.name || g.id}
                                {isAllStaff && (
                                  <span className="ml-2 text-xs text-blue-600">(All Staff)</span>
                                )}
                              </span>
                              {g.isSystem && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                  System
                                </span>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {selectedGroupIds.length === 0 && (
                  <div className="mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                    ⚠️ No groups selected — End Users won't see this item (Admin/Owner only).
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-300 bg-white hover:bg-slate-50"
            disabled={saving || uploadingImage}
          >
            Cancel
          </button>
          <button
            type="submit"
            form={formId}
            className="px-4 py-2 text-sm font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            disabled={saving || uploadingImage}
          >
            {uploadingImage ? "Uploading..." : saving ? "Creating..." : "Create Product"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryCreateModal;
